import Sample from "../models/sample"

import { observable, action, computed, autorun } from "mobx"
import _ from "lodash"
import axios from "axios"

class SampleStore {
  @observable samples = []
  @observable sampleSlugs = new Set()
  @observable selectedSamples = []
  @observable selectedSamplesForSequencer = []
  @observable trendingSamples = []
  @observable onlySaveSelected = false
  @observable canPlayMultipleSamplesAtOnce = false
  @observable showSampleSelect = false
  @observable showBatchEditButton = false
  @observable showSequencer = false

  constructor() {
    if (typeof window !== 'undefined') {

      this.loadFromLocalStorage();

      // Auto-save to localStorage whenever relevant properties change
      autorun(() => {
        this.saveToLocalStorage();
      });
    }
  }

  @action
  loadFromLocalStorage = () => {
    const showSequencer = localStorage.getItem("showSequencer");
    if (showSequencer) {
      this.showSequencer = JSON.parse(showSequencer);
    } else {
      this.showSequencer = false;
      return;
    }

    const selectedSamplesData = localStorage.getItem("selectedSamplesForSequencer");
    if (selectedSamplesData) {
      const samplesData = JSON.parse(selectedSamplesData);

      // Clear current selectedSamples
      this.selectedSamplesForSequencer = [];

      // Map over saved sample data
      samplesData.forEach((sampleData) => {
        // Find the existing Sample instance in samples
        const sample = this.samples.find((s) => s.slug === sampleData.slug);

        if (sample) {
          // Update sample properties if necessary
          sample.isSelectedForSequencer = true; // Mark as selected
          // Update other properties if you want to restore state
          sample.name = sampleData.name;
          sample.category_id = sampleData.category_id;
          sample.tag_list = sampleData.tag_list;
          sample.duration = sampleData.duration;
          sample.slug = sampleData.slug;
          sample.key = sampleData.key;
          sample.mode = sampleData.mode;
          sample.tempo = sampleData.tempo;

          this.selectedSamplesForSequencer.push(sample);
        } else {
          // If sample not found, create new instance and add to both arrays
          const newSample = new Sample(sampleData);
          newSample.isSelectedForSequencer = true;
          this.selectedSamplesForSequencer.push(newSample);
        }
      });
    }
  };

  saveToLocalStorage = () => {
    const serializedSamples = this.selectedSamplesForSequencer.map((sample) => sample.toJSON());
    localStorage.setItem("selectedSamplesForSequencer", JSON.stringify(serializedSamples));
    localStorage.setItem("showSequencer", JSON.stringify(this.showSequencer));
  };

  @action
  addSample = (sample) => {
    if (!this.sampleSlugs.has(sample.slug)) {
      this.samples.push(sample)
      this.sampleSlugs.add(sample.slug)
    }
  }

  @action
  removeSample = (sample) => {
    if (this.sampleSlugs.has(sample.slug)) {
      this.samples = this.samples.filter((s) => s.slug !== sample.slug)
      this.sampleSlugs.delete(sample.slug)
    }
  }

  @action
  removePublishedSamples = () => {
    const slugsToRemove = new Set();
    this.samples = this.samples.filter((s) => {
      if (s.shouldBePublished) {
        slugsToRemove.add(s.slug);
        return false;
      }
      return true;
    });
    slugsToRemove.forEach(slug => this.sampleSlugs.delete(slug));

    this.selectedSamples = this.selectedSamples.filter(
      (s) => s.shouldBePublished === false
    )
  }

  @action
  selectSample = (sample) => {
    sample.select()
    this.selectedSamples.push(sample)
  }

  @action
  selectSampleForSequencer = (sample) => {
    sample.selectForSequencer()
    this.selectedSamplesForSequencer.push(sample)
  }


  @action
  deselectSample = (sample) => {
    sample.deselect()
    this.selectedSamples = this.selectedSamples.filter(
      (s) => s.slug !== sample.slug
    )
  }

  @action
  deselectSampleForSequencer = (sample) => {
    sample.deselectForSequencer()
    this.selectedSamplesForSequencer = this.selectedSamplesForSequencer.filter(
      (s) => s.slug !== sample.slug
    )
  }


  @action
  clearStore = () => {
    this.selectedSamples.forEach((sample) => {
      sample.deselect()
    })
    this.selectedSamples = []
  }

  @action
  resetStore = () => {
    this.clearStore()
    this.samples = []
    this.sampleSlugs.clear()
  }

  @action
  assignCategoryId = (categoryId) => {
    this.selectedSamples.forEach((sample) => {
      sample.category_id = parseInt(categoryId)
    })
  }

  @action
  assignInputChange = (inputEvent) => {
    this.selectedSamples.forEach((sample) => {
      sample[inputEvent.target.name] = inputEvent.target.value
    })
  }

  @computed
  get categoryIds() {
    const categoryIds = this.selectedSamples.map((sample) => sample.category_id)
    return _.uniq(categoryIds)
  }

  @action
  assignTag = (tag) => {
    this.selectedSamples.forEach((sample) => {
      sample.addTag(tag)
    })
  }

  @action
  assignName = (name) => {
    this.selectedSamples.forEach((sample) => {
      sample.name = name
    })
  }

  @action
  touchField = (field) => {
    this.selectedSamples.forEach((sample) => {
      sample.touchField(field)
    })
  }

  @action
  removeTag = (tag) => {
    this.selectedSamples.forEach((sample) => {
      sample.removeTag(tag)
    })
  }

  @action
  removeCyaniteTag = (tag) => {
    this.selectedSamples.forEach((sample) => {
      sample.removeCyaniteTag(tag)
    })
  }

  @action
  clearTags = () => {
    this.selectedSamples.forEach((sample) => {
      sample.clearTags()
    })
  }

  @action
  toggleSampleSelect = () => {
    this.showSampleSelect = !this.showSampleSelect;
  }

  @action
  toggleSequencer = () => {
    this.showSequencer = !this.showSequencer;
    if (!this.showBatchEditButton) {
      this.toggleSampleSelect()
    }
  }

  @computed
  get sampleCount() {
    return this.samples.length
  }

  @computed
  get averageTagCount() {
    const totalTags = this.samples.reduce((totalTagCount, sample) => {
      return totalTagCount + sample.tag_list.length
    }, 0)
    return totalTags / this.sampleCount
  }

  @computed
  get sampleIds() {
    return this.samples.map((s) => s.id)
  }

  @computed
  get selectedSampleIds() {
    return this.selectedSamples.map((s) => s.id)
  }

  @computed
  get selectedSampleCount() {
    return this.selectedSamples.length
  }

  @computed
  get commonValidationErrors() {
    const errors = {}
    const fieldsToCheck = Sample.FIELDS_TO_VALIDATE
    fieldsToCheck.forEach((fieldName) => {
      if (
        _.every(this.selectedSamples, function (sample) {
          return sample.validationErrors[`${fieldName}`] ? true : false
        })
      ) {
        errors[`${fieldName}`] = true
      }
    })

    return errors
  }

  @computed
  get commonTouchedFields() {
    const touchedFields = {}
    const fieldsToCheck = Sample.FIELDS_TO_VALIDATE
    fieldsToCheck.forEach((fieldName) => {
      if (
        _.every(this.selectedSamples, function (sample) {
          return sample.touchedFields[`${fieldName}`] ? true : false
        })
      ) {
        touchedFields[`${fieldName}`] = true
      }
    })

    return touchedFields
  }

  @computed
  get anyValidationErrors() {
    let validationErrorPresent = false
    let samples

    if (this.onlySaveSelected) {
      samples = this.selectedSamples
    } else {
      samples = this.samples
    }

    samples.map((sample) => {
      if (!_.isEmpty(sample.validationErrors)) {
        validationErrorPresent = true
      }
    })
    return validationErrorPresent
  }

  containsSample(sample) {
    var sampleSlugs = this.selectedSamples.map((sample) => sample.slug)
    return sampleSlugs.includes(sample.slug)
  }

  @computed
  get tagsInCommon() {
    const allTags = this.selectedSamples.map((sample) => sample.tag_list)
    return _.intersection(...allTags)
  }

  @computed
  get areSelectedPublished() {
    const selectedSamplesShouldBePublished = this.selectedSamples.map(
      (sample) => sample.shouldBePublished
    )
    const valuesOfShouldBePublished = _.uniq(selectedSamplesShouldBePublished)
    if (valuesOfShouldBePublished.length === 1) {
      return valuesOfShouldBePublished[0]
    } else {
      return null
    }
  }

  getCyaniteTagsInCommon(tagType) {
    const allTags = this.selectedSamples.map((sample) =>
      _.difference(sample.cyaniteTags[tagType], sample.tag_list)
    )
    return _.intersection(...allTags)
  }

  async getSamples(samples_url, csrfToken) {
    axios.defaults.headers.common["Accept"] = "application/json"
    axios.defaults.headers.common["X-CSRF-Token"] = csrfToken

    try {
      const response = await axios({
        url: samples_url,
        responseType: "json",
      })
      response.data.samples.forEach((sampleJSON) => {
        const sample = new Sample(sampleJSON)
        this.addSample(sample)
      })
      return true
    } catch (error) {
      console.error("getSamples error", error)
      return false
    }
  }

  async saveSamples(samples, csrfToken) {
    const hasErrors = this.anyValidationErrors
    if (hasErrors) {
      console.log("Error on save", hasErrors)
      return false
    }

    axios.defaults.headers.common["X-CSRF-Token"] = csrfToken

    try {
      let samplesData = []
      samples.forEach((sample) => {
        const sampleData = {
          id: sample.id,
          category_id: sample.category_id,
          name: sample.name,
          tag_list: sample.tag_list,
          tempo: sample.tempo,
          key: sample.key,
          mode: sample.mode,
          publish: sample.shouldBePublished,
        }
        samplesData.push(sampleData)
      })

      const response = await axios({
        method: "patch",
        url: "/api/samples/batch_update",
        timeout: 25000,
        data: {
          samples: samplesData,
          authenticity_token: csrfToken,
        },
      })

      return response
    } catch (error) {
      console.error("save error", error)
      return error
    }
  }

  @action
  async getSuggestedNames({setNameToSuggested = false}) {

    const promises = this.selectedSamples.map((sample) => {
      return sample.getSuggestedName({setNameToSuggested})
    })

    await Promise.all(promises);
  }
}

export default new SampleStore()